import React from 'react';
import { Grid, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DialogContent from 'components/dialog/DialogContent';
import DialogTitle from 'components/dialog/DialogTitle';
import DialogActions from 'components/dialog/DialogActions';
import Button from 'components/button/Base';
import * as importActions from 'gridUI/actions/import';
import * as gridActions from '../actions';
import { useDispatch } from 'react-redux';
import InputText from 'components/inputs/InputText';
import IOSSwitch from 'components/switches/IOS';
import ViewsSwitch from 'components/switches/ViewsSwitch';
import { SYSTEM_COLUMN_IDS, UPLOAD_SETUP_VIEW_TYPES, RECORD_IDENTIFIER_TYPE } from 'const';
import { AutoSizer } from 'react-virtualized-dn';
import GridUITable from 'gridUI/table/grid/GridUIViewOnly';
import { useGridScratchImport } from 'hooks/gridUI/import';
import { ROW_HEIGHT, COLUMN_ID_WIDTH } from 'const/gridUI';
import ColumnMapList from './ColumnMapList';
import ImportHeader from './ImportHeader';
import { sendManualTrack } from 'tracker';
import Spinner from 'components/spinner/Base';
import { isKbEscape, isKbEnter, isKbShiftEnter } from 'utils/keyboard';
import { useTranslation } from 'react-i18next';
import { formatReviewData } from 'utils/gridUI/data';
import * as columnTypes from 'const/columnTypes';
import { getServerTimeZoneValue } from 'const/time';
import Checkbox from 'components/checkbox/Base';
import QuestionSVG from 'assets/images/svg/QuestionSVG';
import Tooltip from 'components/tooltip/Base';
import AccessControl from 'auth/AccessControl';
import * as roleConst from 'auth/roleConst';

const useStyles = makeStyles(theme => ({
    root: {
        width: 850
    },
    btnSubmit: {
        width: 120,
        position: 'relative'
    },
    buttonProgress: {
        color: theme.palette.primary.main,
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12
    },
    numberStep: {
        width: 16,
        minWidth: 16,
        height: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.colors.lightLavender,
        color: theme.colors.white,
        borderRadius: '50%',
        fontSize: 10,
        marginRight: theme.spacing(1),
        position: 'relative'
    },
    table: {
        height: 361,
        userSelect: 'none',
        '& .cell': {
            pointerEvents: 'none'
        },
        position: 'relative',
        width: '100%'
    },
    loadingSpinner: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.colors.white,
        zIndex: 10
    },
    displayFlex: {
        display: 'flex'
    }
}));

function GridUploadSetup({ files, dbId, gridName, onNameChange, onClose, onSetupSwitch }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const [isUploading, setIsUploading] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [isContainFirstRowHeader, setIsContainFirstRowHeader] = React.useState(true);
    const [viewType, setViewType] = React.useState(UPLOAD_SETUP_VIEW_TYPES.TABLE);
    const [isTranslation, setIsTranslation] = React.useState(false);
    const gridScratchImport = useGridScratchImport();
    const [nonUnique, setNonUnique] = React.useState(false);
    const [globalUnique, setGlobalUnique] = React.useState(false);

    const data = React.useMemo(() => {
        return gridScratchImport?.data || {};
    }, [gridScratchImport]);

    const rows = React.useMemo(() => {
        return gridScratchImport?.rows || [];
    }, [gridScratchImport]);

    const metaData = React.useMemo(() => {
        return gridScratchImport?.metaData || {};
    }, [gridScratchImport]);

    const columns = React.useMemo(() => {
        return gridScratchImport?.columns || [];
    }, [gridScratchImport]);

    const columnMap = React.useMemo(() => {
        return gridScratchImport?.columnMap || {};
    }, [gridScratchImport]);

    const resourceKey = React.useMemo(() => {
        return gridScratchImport?.resourceKey;
    }, [gridScratchImport]);

    React.useEffect(() => {
        if (isTranslation) {
            setTimeout(() => {
                onSetupSwitch();
            }, 100);
        }
    }, [isTranslation, onSetupSwitch]);

    React.useEffect(() => {
        if (files) {
            setIsUploading(true);
            dispatch(
                importActions.importGridReadFile({
                    dbId,
                    file: files,
                    successCallback: () => {
                        setIsUploading(false);
                    },
                    errorCallback: () => {
                        setIsUploading(false);
                    }
                })
            );
        }
    }, [dispatch, dbId, files]);

    const handleNonUniqueChange = React.useCallback(
        e => {
            const nonUniqueChecked = e.target.checked;
            setNonUnique(nonUniqueChecked);
            if (nonUniqueChecked && globalUnique) {
                setGlobalUnique(false);
            }
        },
        [globalUnique]
    );

    const handleGlobalUniqueChange = React.useCallback(
        e => {
            const globalUniqueChecked = e.target.checked;
            setGlobalUnique(globalUniqueChecked);
            if (globalUniqueChecked && nonUnique) {
                setNonUnique(false);
            }
        },
        [nonUnique]
    );

    const handleViewChange = type => {
        setViewType(type);
    };

    const handleSwitchFirstRowChange = e => {
        setIsContainFirstRowHeader(e.target.checked);
    };

    const totalRecords = React.useMemo(() => {
        return rows.length > 5 ? 6 : rows.length;
    }, [rows]);

    const isValidColumnsName = React.useMemo(() => {
        let isValid = true;
        const keys = Object.keys(columnMap);

        for (let key of keys) {
            const detail = columnMap?.[key];
            if (!detail?.columnName) {
                isValid = false;
                break;
            }
        }
        return isValid;
    }, [columnMap]);

    const isDuplicateName = React.useMemo(() => {
        const names = Object.keys(columnMap)?.map(key => columnMap?.[key]?.columnName);
        const uniqueNames = [...new Set([...names])];

        return uniqueNames?.length !== names?.length;
    }, [columnMap]);

    const isValidCreate = React.useMemo(() => {
        return !!gridName && isValidColumnsName && !isDuplicateName;
    }, [gridName, isValidColumnsName, isDuplicateName]);

    const handleImportGrid = React.useCallback(() => {
        sendManualTrack({ type: `Start Add Grid By Upload File` });

        const columnMappings = [];

        for (const columnId of columns) {
            const column = columnMap?.[columnId];

            if (column) {
                if (SYSTEM_COLUMN_IDS?.includes(column?.columnType)) {
                    column.columnId = column?.columnType;
                    delete column?.columnType;
                }

                if (columnTypes.DATETIME === column?.columnType && column?.columnTimezone) {
                    column.columnMappingOption = {
                        timezone: getServerTimeZoneValue(column.columnTimezone)
                    };
                    delete column.columnTimezone;
                }

                columnMappings.push(column);
            }
        }

        setIsSubmitting(true);

        dispatch(
            gridActions.createGridByImport({
                resourceKey,
                dbId,
                body: {
                    columnMappings,
                    gridName: gridName,
                    description: t('localization_grid_default_description'),
                    withHeader: isContainFirstRowHeader ? true : false,
                    recordIdentifierType: nonUnique
                        ? RECORD_IDENTIFIER_TYPE.pathTagRecordId
                        : RECORD_IDENTIFIER_TYPE.recordId,
                    globalUniquePublicRecordId: globalUnique
                },
                successCallback: () => {
                    console.log('successCallback');
                    setIsSubmitting(false);
                    onClose && onClose();
                    sendManualTrack({ type: `Add Grid By Upload File Success` });
                },
                errorCallback: () => {
                    console.log('errorCallback');
                    setIsSubmitting(false);
                    sendManualTrack({ type: `Add Grid By Upload File Failed` });
                }
            })
        );
    }, [
        t,
        dbId,
        dispatch,
        onClose,
        resourceKey,
        columnMap,
        isContainFirstRowHeader,
        gridName,
        columns,
        nonUnique,
        globalUnique
    ]);

    const handleKeyDown = e => {
        if (isKbEscape(e)) {
            onClose && onClose();
        }
        if (isKbEnter(e) && !isKbShiftEnter(e)) {
            e.preventDefault();
            e.stopPropagation();
            if (isValidCreate) {
                handleImportGrid();
            }
        }
    };

    const fileData = React.useMemo(() => {
        return formatReviewData({ data, metaData });
    }, [data, metaData]);

    return (
        <>
            <DialogTitle title={t('import_data')} onClose={onClose} />
            <DialogContent>
                <Grid container direction="column" className={classes.root} spacing={4} wrap="nowrap">
                    <Grid item container direction="column" spacing={2}>
                        <Grid item>
                            <Typography variant="body1">{t('grid_form_name_label')}</Typography>
                        </Grid>
                        <Grid item>
                            <InputText
                                placeholder={t('localization_grid_name_input')}
                                name={t('grid_form_name_label')}
                                value={gridName}
                                error={!gridName}
                                errorText={t('global_this_field_is_required')}
                                disabled={isSubmitting}
                                onChange={e => onNameChange(e.target.value)}
                                autoFocus={true}
                                onKeyDown={handleKeyDown}
                            />
                        </Grid>
                    </Grid>

                    <Grid item container alignItems="center" direction="row" spacing={2}>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center" wrap="nowrap">
                                <Grid item>
                                    <IOSSwitch
                                        checked={isTranslation}
                                        onChange={() => setIsTranslation(!isTranslation)}
                                        className={classes.switchIcon}
                                    />
                                </Grid>
                                <Grid item>
                                    <Typography variant="body1">{t('import_with_translation_option')}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item>
                        <Grid container spacing={1} direction="column" justify="space-between">
                            <Grid item>
                                <Typography variant="body1">{t('localization_mapping_column_label')}</Typography>
                            </Grid>
                            <Grid container alignItems="center" justify="space-between">
                                <Grid item>
                                    <Grid container alignItems="center" direction="row">
                                        <Grid item>
                                            <Grid container direction="row" alignItems="center" spacing={2}>
                                                <Grid item>
                                                    <IOSSwitch
                                                        checked={isContainFirstRowHeader}
                                                        onChange={handleSwitchFirstRowChange}
                                                        className={classes.switchIcon}
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant="body2">
                                                        {t('localization_first_row_as_header_check')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <ViewsSwitch value={viewType} onChange={handleViewChange} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    {viewType === UPLOAD_SETUP_VIEW_TYPES.TABLE && (
                        <Grid item>
                            <Grid container className={classes.table}>
                                {isUploading && (
                                    <div className={classes.loadingSpinner}>
                                        <Spinner size={30} thick={4} />
                                    </div>
                                )}
                                {!isUploading && (
                                    <AutoSizer>
                                        {({ width, height }) => {
                                            return (
                                                <GridUITable
                                                    isUseColumnIdByIndex={true}
                                                    metaData={metaData}
                                                    totalRecords={totalRecords}
                                                    data={fileData}
                                                    rows={rows}
                                                    columns={columns}
                                                    width={width}
                                                    maxHeight={height}
                                                    rowHeight={ROW_HEIGHT}
                                                    fixedColumnCount={0}
                                                    fixedRowCount={1}
                                                    COLUMN_ID_WIDTH={COLUMN_ID_WIDTH}
                                                    isFirstRowHeader={isContainFirstRowHeader}
                                                    headerRenderer={headerProps => {
                                                        const columnId = columns?.[headerProps?.columnIndex];
                                                        return (
                                                            <ImportHeader
                                                                {...headerProps}
                                                                onChange={() => {}}
                                                                columnId={columnId}
                                                                columnMap={columnMap}
                                                            />
                                                        );
                                                    }}
                                                    nonHeader
                                                    AGG_HEIGHT={0}
                                                />
                                            );
                                        }}
                                    </AutoSizer>
                                )}
                            </Grid>
                        </Grid>
                    )}
                    {viewType === UPLOAD_SETUP_VIEW_TYPES.LIST && (
                        <Grid item className={classes.columnMapList}>
                            <ColumnMapList
                                columnMap={columnMap}
                                metaData={metaData}
                                isContainFirstRowHeader={isContainFirstRowHeader}
                                columns={columns}
                            />
                        </Grid>
                    )}
                    <AccessControl view={roleConst.SUPPORT_PATHTAG_UNIQUE_RECORDID}>
                        <Grid item container direction="column" style={{ marginTop: 14 }}>
                            <Grid item style={{ marginBottom: 8 }}>
                                <Typography variant="body1">{t('advanced_options')}</Typography>
                            </Grid>
                            <Grid item container>
                                <Grid item container xs={4} alignItems="center">
                                    <Grid item>
                                        <Checkbox
                                            checked={nonUnique}
                                            onChange={handleNonUniqueChange}
                                            style={{ padding: 0, marginRight: 8 }}
                                        />
                                    </Grid>
                                    <Grid item style={{ marginRight: 8 }}>
                                        <Typography variant="body2">{t('allow_non_unique_record_ids')}</Typography>
                                    </Grid>
                                    <Tooltip title="You can import non-unique record IDs if you map unique Path tags for each record">
                                        <Grid item className={classes.displayFlex}>
                                            <QuestionSVG />
                                        </Grid>
                                    </Tooltip>
                                </Grid>
                                <Grid item container xs={4} alignItems="center">
                                    <Grid item>
                                        <Checkbox
                                            checked={globalUnique}
                                            onChange={handleGlobalUniqueChange}
                                            style={{ padding: 0, marginRight: 8 }}
                                        />
                                    </Grid>
                                    <Grid item style={{ marginRight: 8 }}>
                                        <Typography variant="body2">{t('global_unique_record_ids')}</Typography>
                                    </Grid>
                                    <Tooltip title="Record IDs will be auto-generated and cannot be edited to ensure the uniqueness">
                                        <Grid item className={classes.displayFlex}>
                                            <QuestionSVG />
                                        </Grid>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                    </AccessControl>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container wrap="nowrap" spacing={2} alignItems="center" justify="flex-end">
                    <Grid item>
                        <Button onClick={onClose} width={120} variant="outlined">
                            {t('global_cancel')}
                        </Button>
                    </Grid>
                    <Grid item>
                        <div className={classes.btnSubmit}>
                            <Button
                                onClick={handleImportGrid}
                                disabled={isSubmitting || !isValidCreate}
                                width={120}
                                variant="contained"
                            >
                                {t('global_create')}
                            </Button>
                            {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                        </div>
                    </Grid>
                </Grid>
            </DialogActions>
        </>
    );
}

export default React.memo(GridUploadSetup);
